/**
 * DocumentViewControls — everything you can ask of the document on screen,
 * in the two groups the header puts in two different places.
 *
 * `DocumentModeToggles` is the middle of the bar, beside the document's name:
 * which version of it you are reading. `DocumentActions` is the far right:
 * what you can do with it. They were one component in one corner, which is
 * why the bar read as a pile of controls rather than a sentence about the
 * document.
 *
 * These used to be a custom header INSIDE the side panel, which is why they
 * disappeared with it. They live outside the pane now, in the session's
 * toolbar strip, for one reason: expanding the document to the whole window
 * replaces the pane, and a control that lived in the pane would vanish at
 * exactly the moment a reviewer settles in to read. The same component
 * renders in the strip and in the expanded view's bar.
 *
 * They own NO state. Every toggle is driven from the session, so the mode
 * survives the document being expanded, collapsed or reopened.
 *
 * WHAT IS DELIBERATELY ABSENT: a Close, and a findings toggle. Collapsing
 * exits a MODE and leaving the document is the breadcrumb's job; the findings
 * pane has its own toggle on the left of the bar, next to the risk readout,
 * because that is where the session's own state is read.
 */

import React from 'react';
import clsx from 'clsx';
import { Menu } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import {
  PiArrowsInLight,
  PiArrowsOutLight,
  PiCaretDownLight,
  PiCheckLight,
  PiDownloadSimpleLight,
} from 'react-icons/pi';
import { RevisionStyle, ViewSource, supportsRevision } from './documentView';

interface ModeProps {
  fileType?: string | null;
  viewSource: ViewSource;
  onViewSourceChange: (source: ViewSource) => void;
  revisionStyle: RevisionStyle;
  onRevisionStyleChange: (style: RevisionStyle) => void;
}

interface ActionProps {
  onDownload: () => void;
  /** Toggles reading mode. The same control both ways — see below. */
  onToggleExpand: () => void;
  expanded: boolean;
}

/** One segmented-toggle pill. `.nav-pill` is the platform's tab treatment. */
const TogglePill: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testId?: string;
}> = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    data-active={active}
    aria-pressed={active}
    className={clsx(
      'nav-pill rounded-full px-2.5 py-1 text-[11px] font-medium leading-none',
      'transition-colors duration-200 ease-slide',
      active ? 'nav-pill-active text-fg-strong' : 'text-fg-muted hover:text-fg-body',
    )}
  >
    {children}
  </button>
);

/** The container a pair of toggles sits in, so the pair reads as one control. */
const ToggleGroup: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div
    role="group"
    aria-label={label}
    className="flex items-center gap-0.5 p-0.5 rounded-full
               bg-surface-sunken border border-hairline-soft flex-shrink-0"
  >
    {children}
  </div>
);

/**
 * Which version of the document: original, or what the decisions produced.
 *
 * TWO SHAPES, ONE CONTROL. Wide, it is two segmented groups you can read at a
 * glance and hit in one press. Below `xl` the right-hand cluster — two toggle
 * groups, a divider and two icon buttons — no longer fits beside the
 * document's name without the header having to choose between overlapping and
 * overflowing, so the same four choices collapse into one menu button that
 * states the current mode. Nothing is lost and nothing is hidden behind a
 * width; it just costs a press.
 *
 * The breakpoint is `xl` (1280px) because that is where the measurement says
 * it stops fitting, not because it is a round number: the cluster is ~340px,
 * the name takes up to 420, the readout ~150, and the page has 1072 to give
 * at 1280 once the sidebar is out.
 */
export const DocumentModeToggles: React.FC<ModeProps> = ({
  fileType,
  viewSource,
  onViewSourceChange,
  revisionStyle,
  onRevisionStyleChange,
}) => {
  const { t } = useTranslation();

  // Clean/Redline is meaningless on a format with nowhere to put a redline,
  // and on the original there is nothing to have changed.
  const revisable = supportsRevision(fileType);
  if (!revisable) return null;

  /** What the compact button says it is showing. */
  const modeLabel = viewSource === 'original'
    ? t('Original')
    : `${t('Revised')} · ${revisionStyle === 'clean' ? t('Clean') : t('Redline')}`;

  return (
    <>
      {/* ── Wide: the segmented groups ───────────────────────────────── */}
      <div className="hidden xl:flex items-center gap-2" data-testid="document-mode-toggles">
        <ToggleGroup label={t('Document version')}>
          <TogglePill
            active={viewSource === 'original'}
            onClick={() => onViewSourceChange('original')}
            testId="doc-source-original"
          >
            {t('Original')}
          </TogglePill>
          <TogglePill
            active={viewSource === 'revised'}
            onClick={() => onViewSourceChange('revised')}
            testId="doc-source-revised"
          >
            {t('Revised')}
          </TogglePill>
        </ToggleGroup>

        {viewSource === 'revised' && (
          <ToggleGroup label={t('Revision style')}>
            <TogglePill
              active={revisionStyle === 'clean'}
              onClick={() => onRevisionStyleChange('clean')}
              testId="doc-style-clean"
            >
              {t('Clean')}
            </TogglePill>
            <TogglePill
              active={revisionStyle === 'redline'}
              onClick={() => onRevisionStyleChange('redline')}
              testId="doc-style-redline"
            >
              {t('Redline')}
            </TogglePill>
          </ToggleGroup>
        )}
      </div>

      {/* ── Narrow: the same four choices, one press away ─────────────── */}
      <Menu as="div" className="relative xl:hidden">
        {({ open }) => (
          <>
            <Menu.Button
              data-testid="document-mode-menu"
              data-open={open}
              title={t('Document version')}
              className={clsx(
                'flex items-center gap-1.5 rounded-full border px-2.5 py-1',
                'text-[11px] font-medium leading-none whitespace-nowrap',
                'transition-colors duration-200 ease-slide',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40',
                open
                  ? 'bg-surface-sunken border-champagne/40 text-fg-strong'
                  : 'bg-surface-sunken/60 border-hairline-soft text-fg-body hover:bg-surface-sunken',
              )}
            >
              {modeLabel}
              <PiCaretDownLight
                className={clsx(
                  'w-3 h-3 flex-shrink-0 transition-transform duration-200 ease-slide',
                  open && 'rotate-180',
                )}
                aria-hidden
              />
            </Menu.Button>

            {/* Anchored so it escapes the toolbar's `overflow-hidden`, the
                same way the document picker does. */}
            <Menu.Items
              anchor="bottom end"
              transition
              className="[--anchor-gap:6px] z-[300] min-w-[11rem] rounded-xl bg-surface p-1
                         ring-1 ring-black/[0.05] dark:ring-white/[0.08] shadow-xl
                         focus:outline-none origin-top transition duration-150 ease-pop
                         data-[closed]:opacity-0 data-[closed]:scale-95"
            >
              <MenuChoice
                label={t('Original')}
                active={viewSource === 'original'}
                onClick={() => onViewSourceChange('original')}
              />
              <MenuChoice
                label={`${t('Revised')} · ${t('Clean')}`}
                active={viewSource === 'revised' && revisionStyle === 'clean'}
                onClick={() => { onViewSourceChange('revised'); onRevisionStyleChange('clean'); }}
              />
              <MenuChoice
                label={`${t('Revised')} · ${t('Redline')}`}
                active={viewSource === 'revised' && revisionStyle === 'redline'}
                onClick={() => { onViewSourceChange('revised'); onRevisionStyleChange('redline'); }}
              />
            </Menu.Items>
          </>
        )}
      </Menu>

      {/* Separates the toggles from the icon buttons beside them. It lives
          HERE, not in the header, because only this component knows whether
          the file type has versions to toggle at all — a hairline next to
          nothing is what the header would have drawn. */}
      <span aria-hidden className="h-5 w-px flex-shrink-0 bg-hairline-soft" />
    </>
  );
};

/** One row of the compact mode menu. */
const MenuChoice: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <Menu.Item>
    {() => (
      <button
        type="button"
        onClick={onClick}
        data-active={active}
        className={clsx(
          'flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2',
          'text-left text-[12px] transition-colors duration-200 ease-slide',
          active
            ? 'bg-champagne/10 font-semibold text-fg-strong'
            : 'text-fg-body hover:bg-surface-sunken/70',
        )}
      >
        {label}
        {active && <PiCheckLight className="w-3.5 h-3.5 flex-shrink-0 text-champagne" />}
      </button>
    )}
  </Menu.Item>
);

/** What you can DO with the document — the far right of the bar. */
export const DocumentActions: React.FC<ActionProps> = ({
  onDownload, onToggleExpand, expanded,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-0.5" data-testid="document-actions">
      <button
        type="button"
        onClick={onDownload}
        data-testid="doc-download"
        title={t('Download')}
        aria-label={t('Download')}
        className="flex-shrink-0 p-1.5 rounded-md text-fg-muted
                   hover:bg-surface-sunken/70 hover:text-champagne
                   transition-colors duration-200 ease-slide"
      >
        <PiDownloadSimpleLight className="w-4 h-4" />
      </button>

      {/*
        ONE control, both directions. Reading mode used to be entered here
        and left from a different bar belonging to a different frame, which
        is part of why it felt like two separate states rather than one
        toggle. Same button, same place, and it says which way it goes.
      */}
      <button
        type="button"
        onClick={onToggleExpand}
        data-testid="doc-expand"
        data-expanded={expanded}
        title={expanded ? t('Collapse') : t('Expand')}
        aria-label={expanded ? t('Collapse') : t('Expand')}
        aria-pressed={expanded}
        className="flex-shrink-0 p-1.5 rounded-md text-fg-muted
                   hover:bg-surface-sunken/70 hover:text-champagne
                   transition-colors duration-200 ease-slide"
      >
        {expanded
          ? <PiArrowsInLight className="w-4 h-4" />
          : <PiArrowsOutLight className="w-4 h-4" />}
      </button>
    </div>
  );
};

export default DocumentModeToggles;
